'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { getTextStyles } from '../theme/tokens';
import { DataTableTheme } from '../types';

/**
 * Props for the TextCell component
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */
interface TextCellProps {
  /**
   * The value to display as text
   * Can be any type, will be converted to string
   * If null or undefined, displays "-" as placeholder
   *
   * @example "John Doe"
   * @example 12345
   * @example true
   */
  value: any;

  /**
   * Whether to truncate long text with ellipsis
   * When true:
   * - Adds CSS truncate class (overflow hidden, text-overflow ellipsis)
   * - Shows full text on hover via title attribute
   * - Prevents text wrapping to multiple lines
   *
   * When false:
   * - Text behavior controlled by wrap prop
   *
   * @default false
   */
  truncate?: boolean;

  /**
   * Control text wrapping behavior (when truncate is false)
   * - 'wrap': Allow text to wrap to multiple lines
   * - 'nowrap': Prevent wrapping, text stays on single line
   *
   * @default 'nowrap'
   */
  wrap?: 'wrap' | 'nowrap';

  /**
   * Horizontal text alignment
   *
   * - 'left': Left-aligned text (text-left)
   * - 'center': Centered text (text-center)
   * - 'right': Right-aligned text (text-right)
   *
   * This should match the column's alignment prop for visual consistency
   *
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Additional CSS classes to apply to the text container
   */
  className?: string;

  /**
   * Theme to use for styling (provided by parent)
   */
  theme?: DataTableTheme;
}

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const TextCell: React.FC<TextCellProps> = ({
  value,
  truncate = false,
  wrap = 'nowrap',
  align = 'left',
  className,
  theme,
}) => {
  const displayValue = value != null ? String(value) : '-';

  // Get typography styles from theme (inline styles to avoid Tailwind conflicts)
  const textStyles = theme ? getTextStyles(theme) : undefined;

  return (
    <div
      className={cn(
        alignClasses[align],
        !truncate && wrap === 'nowrap' && 'whitespace-nowrap',
        className
      )}
      style={{
        ...(truncate ? {
          overflow: 'clip',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        } : {}),
        ...textStyles?.primary(),
      }}
      title={truncate ? displayValue : undefined}
    >
      {displayValue}
    </div>
  );
};
